/**
 * Prediction Watchlist Page — "what should Aaron watch today?"
 *
 * Three sections (Deals / Investors / Companies) of cards generated
 * daily by the backend at 06:00 UTC. Each card shows the entity name,
 * a priority badge color-coded by score bucket, the 1-line "why this
 * matters today" reason, and a link to the entity's detail page.
 *
 * The macro snapshot summary is collapsible at the top of the page so
 * Aaron can read the underlying state of the world.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  Sparkles,
  Briefcase,
  Users,
  Building2,
} from 'lucide-react';

import DashboardLayout from '../components/DashboardLayout';
import watchlistService, {
  WatchlistEntry,
  WatchlistPayload,
} from '../services/watchlist';

function priorityBucket(p: number): {
  label: string;
  badge: string;
  bar: string;
} {
  if (p >= 80) return { label: 'Critical', badge: 'bg-red-100 text-red-800 border-red-300', bar: 'bg-red-500' };
  if (p >= 60) return { label: 'High', badge: 'bg-orange-100 text-orange-800 border-orange-300', bar: 'bg-orange-500' };
  if (p >= 40) return { label: 'Medium', badge: 'bg-yellow-100 text-yellow-800 border-yellow-300', bar: 'bg-yellow-500' };
  if (p >= 20) return { label: 'Low', badge: 'bg-blue-100 text-blue-800 border-blue-300', bar: 'bg-blue-500' };
  return { label: 'Watch', badge: 'bg-gray-100 text-gray-700 border-gray-300', bar: 'bg-gray-400' };
}

const WatchlistCard: React.FC<{ entry: WatchlistEntry }> = ({ entry }) => {
  const bucket = priorityBucket(entry.priority);
  return (
    <Link
      to={entry.href}
      className="bg-white rounded-xl border border-gray-200 p-4 hover:border-purple-400 hover:shadow-md transition-all flex flex-col group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="font-semibold text-gray-900 line-clamp-2">{entry.name}</div>
        <span
          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap ${bucket.badge}`}
        >
          {bucket.label} · {entry.priority}
        </span>
      </div>
      <div className="w-full h-1 bg-gray-100 rounded mb-3 overflow-hidden">
        <div
          className={`h-1 ${bucket.bar}`}
          style={{ width: `${Math.max(2, entry.priority)}%` }}
        />
      </div>
      <div className="text-sm text-gray-700 line-clamp-3 flex-1">{entry.reason}</div>
      <div className="mt-3 flex items-center justify-end text-xs text-purple-600 group-hover:text-purple-800">
        View details <ArrowRight className="w-3 h-3 ml-1" />
      </div>
    </Link>
  );
};

const Section: React.FC<{
  title: string;
  icon: React.ReactNode;
  entries: WatchlistEntry[];
  emptyMsg: string;
}> = ({ title, icon, entries, emptyMsg }) => (
  <section className="space-y-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <div className="p-1.5 bg-purple-100 rounded-lg text-purple-700">{icon}</div>
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        <span className="text-xs text-gray-500">({entries.length})</span>
      </div>
    </div>
    {entries.length === 0 ? (
      <p className="text-sm text-gray-500 italic">{emptyMsg}</p>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {entries.map((e) => (
          <WatchlistCard key={e.id} entry={e} />
        ))}
      </div>
    )}
  </section>
);

const PredictionWatchlistPage: React.FC = () => {
  const [data, setData] = useState<WatchlistPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [macroOpen, setMacroOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const payload = await watchlistService.fetchAll();
      setData(payload);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Failed to load watchlist';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const result = await watchlistService.regenerate();
      const total =
        (result.counts?.deal || 0) +
        (result.counts?.investor || 0) +
        (result.counts?.company || 0);
      toast.success(`Watchlist regenerated (${total} entries)`);
      await load();
    } catch (err: any) {
      toast.error('Regeneration failed');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              Prediction Watchlist
            </h1>
            <p className="text-gray-600 mt-1">
              The deals, investors, and companies most worth attention right now,
              given current macro conditions. Refreshed daily at 06:00 UTC.
            </p>
            {data?.as_of && (
              <p className="text-xs text-gray-500 mt-1">
                As of {new Date(data.as_of).toLocaleString()}
              </p>
            )}
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-300 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {/* Macro snapshot — collapsible */}
        <div className="bg-purple-50 border border-purple-200 rounded-xl">
          <button
            onClick={() => setMacroOpen(!macroOpen)}
            className="w-full text-left px-4 py-3 flex items-center justify-between"
          >
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-purple-900">
                Macro snapshot
              </span>
              {data?.macro_summary && (
                <span className="text-sm text-purple-800 line-clamp-1">
                  {data.macro_summary}
                </span>
              )}
            </div>
            {macroOpen ? (
              <ChevronUp className="w-4 h-4 text-purple-700" />
            ) : (
              <ChevronDown className="w-4 h-4 text-purple-700" />
            )}
          </button>
          {macroOpen && (
            <div className="px-4 pb-4 text-sm text-purple-900 space-y-1">
              {data?.macro_summary ? (
                <p>{data.macro_summary}</p>
              ) : (
                <p className="italic text-purple-700">
                  No macro snapshot available yet — try Refresh.
                </p>
              )}
              <p className="text-xs text-purple-700 mt-2">
                Each watchlist entry is scored against this macro context plus
                deterministic signals (stage progression, sector tailwind,
                investor activity, momentum tier).
              </p>
            </div>
          )}
        </div>

        {loading ? (
          <div className="py-20 flex justify-center">
            <Loader2 className="w-6 h-6 text-purple-600 animate-spin" />
          </div>
        ) : !data || data.total === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-purple-300 mb-3" />
            <p className="text-gray-700 font-medium">No watchlist entries yet</p>
            <p className="text-gray-500 text-sm mt-1">
              The first daily refresh runs at 06:00 UTC. Click Refresh to
              generate a watchlist now.
            </p>
          </div>
        ) : (
          <>
            <Section
              title="Deal Watchlist"
              icon={<Briefcase className="w-4 h-4" />}
              entries={data.deals}
              emptyMsg="No open deals scored above the priority threshold today."
            />
            <Section
              title="Investor Watchlist"
              icon={<Users className="w-4 h-4" />}
              entries={data.investors}
              emptyMsg="No investors flagged for outreach this cycle."
            />
            <Section
              title="Company Watchlist"
              icon={<Building2 className="w-4 h-4" />}
              entries={data.companies}
              emptyMsg="No companies hit the signal threshold this cycle."
            />
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PredictionWatchlistPage;
